// Open Bets service — peer-to-peer bets within an activity.
// Dual-write pattern: localStorage primary (sync), DynamoDB fire-and-forget.

import * as db from './db';

const KEY = 'betbuds_open_bets';
const CURRENCIES = ['NOK', 'USD', 'EUR'];

function getStored() {
  const stored = localStorage.getItem(KEY);
  if (stored) return JSON.parse(stored);
  localStorage.setItem(KEY, JSON.stringify([]));
  return [];
}

function save(data) {
  localStorage.setItem(KEY, JSON.stringify(data));
}

function dbWrite(label, promise) {
  promise
    .then((result) => console.log(`[openBets] DynamoDB ${label} succeeded:`, result?.openBetId || result))
    .catch((err) => console.error(`[openBets] DynamoDB ${label} FAILED:`, err));
}

function normalizeCurrency(c) {
  const up = (c || 'NOK').toUpperCase();
  return CURRENCIES.includes(up) ? up : 'NOK';
}

function toDbInput(bet) {
  return {
    openBetId: bet.id,
    activityId: bet.activityId,
    masterEventId: bet.masterEventId,
    createdBy: bet.createdBy,
    title: bet.title,
    description: bet.description || null,
    currency: bet.currency,
    creatorAmount: bet.creatorAmount,
    creatorSide: bet.creatorSide,
    status: bet.status,
    conclusionDate: bet.conclusionDate || null,
    settled: bet.settled,
    settledAt: bet.settledAt || null,
    outcome: bet.outcome || null,
    winningSide: bet.winningSide || null,
    participants: bet.participants,
    comments: bet.comments,
    createdAt: bet.createdAt,
  };
}

function fromDb(record) {
  if (!record) return null;
  const parseJson = (v) => {
    if (v == null) return [];
    if (typeof v === 'string') {
      try { return JSON.parse(v); } catch { return []; }
    }
    return v;
  };
  return {
    id: record.openBetId,
    activityId: record.activityId,
    masterEventId: record.masterEventId,
    createdBy: record.createdBy,
    title: record.title,
    description: record.description || '',
    currency: record.currency || 'NOK',
    creatorAmount: record.creatorAmount || 0,
    creatorSide: record.creatorSide || 'for',
    status: record.status || 'draft',
    conclusionDate: record.conclusionDate || null,
    settled: !!record.settled,
    settledAt: record.settledAt || null,
    outcome: record.outcome || '',
    winningSide: record.winningSide || null,
    participants: parseJson(record.participants),
    comments: parseJson(record.comments),
    createdAt: record.createdAt,
  };
}

export const openBetsService = {
  CURRENCIES,

  getForActivity(activityId) {
    return getStored()
      .filter((b) => b.activityId === activityId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  },

  async fetchForActivity(activityId) {
    try {
      const records = await db.listOpenBetsByActivity(activityId);
      if (records && records.length >= 0) {
        const bets = records.map(fromDb).filter(Boolean);
        // Merge into localStorage: replace bets for this activity with fresh DynamoDB data
        const all = getStored().filter((b) => b.activityId !== activityId);
        save([...all, ...bets]);
        return bets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      }
    } catch (err) {
      console.warn('[openBets] fetchForActivity DynamoDB failed, using localStorage:', err);
    }
    return this.getForActivity(activityId);
  },

  create({ activityId, masterEventId, createdBy, title, description, currency, creatorAmount, creatorSide, conclusionDate, status }) {
    const bet = {
      id: `obet_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      activityId,
      masterEventId,
      createdBy,
      title: title.trim(),
      description: (description || '').trim(),
      currency: normalizeCurrency(currency),
      creatorAmount: Number(creatorAmount) || 0,
      creatorSide: creatorSide === 'against' ? 'against' : 'for',
      status: status === 'live' ? 'live' : 'draft',
      conclusionDate: conclusionDate || null,
      settled: false,
      settledAt: null,
      outcome: '',
      winningSide: null,
      participants: [],
      comments: [],
      createdAt: new Date().toISOString(),
    };

    const all = getStored();
    all.push(bet);
    save(all);

    dbWrite('createOpenBet', db.createOpenBet(toDbInput(bet)));
    return bet;
  },

  update(openBetId, updates) {
    const all = getStored();
    const idx = all.findIndex((b) => b.id === openBetId);
    if (idx === -1) throw new Error('Open bet not found');
    const next = { ...all[idx], ...updates };
    all[idx] = next;
    save(all);

    const dbUpdates = { ...updates };
    if ('participants' in dbUpdates) dbUpdates.participants = next.participants;
    if ('comments' in dbUpdates) dbUpdates.comments = next.comments;
    dbWrite('updateOpenBet', db.updateOpenBet(openBetId, dbUpdates));
    return next;
  },

  publish(openBetId) {
    return this.update(openBetId, { status: 'live' });
  },

  moveToDraft(openBetId) {
    return this.update(openBetId, { status: 'draft' });
  },

  delete(openBetId) {
    save(getStored().filter((b) => b.id !== openBetId));
    dbWrite('deleteOpenBet', db.deleteOpenBet(openBetId));
  },

  joinBet(openBetId, { userId, amount, side, note }) {
    const all = getStored();
    const bet = all.find((b) => b.id === openBetId);
    if (!bet) throw new Error('Open bet not found');
    if (bet.status !== 'live') throw new Error('Bet is not live yet');
    if (bet.settled) throw new Error('Bet is already settled');
    const sum = Number(amount);
    if (!sum || sum <= 0) throw new Error('Enter a positive amount');

    const existingIdx = bet.participants.findIndex((p) => p.userId === userId);
    const entry = {
      userId,
      amount: sum,
      side: side === 'against' ? 'against' : 'for',
      note: (note || '').trim() || null,
      joinedAt: new Date().toISOString(),
    };
    if (existingIdx >= 0) bet.participants[existingIdx] = entry;
    else bet.participants.push(entry);

    save(all);
    dbWrite('updateOpenBet(join)', db.updateOpenBet(openBetId, { participants: bet.participants }));
    return bet;
  },

  leaveBet(openBetId, userId) {
    const all = getStored();
    const bet = all.find((b) => b.id === openBetId);
    if (!bet) throw new Error('Open bet not found');
    if (bet.settled) throw new Error('Bet is already settled');
    bet.participants = bet.participants.filter((p) => p.userId !== userId);
    save(all);
    dbWrite('updateOpenBet(leave)', db.updateOpenBet(openBetId, { participants: bet.participants }));
    return bet;
  },

  addComment(openBetId, { userId, text }) {
    const all = getStored();
    const bet = all.find((b) => b.id === openBetId);
    if (!bet) throw new Error('Open bet not found');
    const trimmed = (text || '').trim();
    if (!trimmed) throw new Error('Comment is empty');
    const comment = {
      id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
      userId,
      text: trimmed,
      createdAt: new Date().toISOString(),
    };
    bet.comments = [...(bet.comments || []), comment];
    save(all);
    dbWrite('updateOpenBet(comment)', db.updateOpenBet(openBetId, { comments: bet.comments }));
    return bet;
  },

  deleteComment(openBetId, commentId) {
    const all = getStored();
    const bet = all.find((b) => b.id === openBetId);
    if (!bet) throw new Error('Open bet not found');
    bet.comments = (bet.comments || []).filter((c) => c.id !== commentId);
    save(all);
    dbWrite('updateOpenBet(delComment)', db.updateOpenBet(openBetId, { comments: bet.comments }));
    return bet;
  },

  settle(openBetId, { winningSide, outcome }) {
    const all = getStored();
    const bet = all.find((b) => b.id === openBetId);
    if (!bet) throw new Error('Open bet not found');
    const validSides = ['for', 'against', 'void'];
    if (!validSides.includes(winningSide)) throw new Error('Invalid winning side');
    bet.settled = true;
    bet.settledAt = new Date().toISOString();
    bet.status = 'settled';
    bet.winningSide = winningSide;
    bet.outcome = (outcome || '').trim();
    save(all);
    dbWrite('updateOpenBet(settle)', db.updateOpenBet(openBetId, {
      settled: true,
      settledAt: bet.settledAt,
      status: 'settled',
      winningSide: bet.winningSide,
      outcome: bet.outcome,
    }));
    return bet;
  },

  unsettle(openBetId) {
    const all = getStored();
    const bet = all.find((b) => b.id === openBetId);
    if (!bet) throw new Error('Open bet not found');
    bet.settled = false;
    bet.settledAt = null;
    bet.status = 'live';
    bet.winningSide = null;
    bet.outcome = '';
    save(all);
    dbWrite('updateOpenBet(unsettle)', db.updateOpenBet(openBetId, {
      settled: false,
      settledAt: null,
      status: 'live',
      winningSide: null,
      outcome: '',
    }));
    return bet;
  },

  // Helper: compute side totals (creator counts as a participant on creatorSide)
  computeTotals(bet) {
    const totals = { for: 0, against: 0 };
    if (bet.creatorAmount > 0) {
      totals[bet.creatorSide === 'against' ? 'against' : 'for'] += Number(bet.creatorAmount) || 0;
    }
    for (const p of bet.participants || []) {
      const side = p.side === 'against' ? 'against' : 'for';
      totals[side] += Number(p.amount) || 0;
    }
    return totals;
  },
};
